import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * Visa master + per-urgency pricing tier (B4.10).
 *
 * Pricing is captured as a child `VisaPricingTier` table - one row per
 * (Visa, urgency). The picker shows tiers as cards on the visa detail.
 */

export const VisaTypeSchema = z.enum([
  'TOURIST',
  'BUSINESS',
  'TRANSIT',
  'WORK',
  'STUDENT',
  'MEDICAL',
  'DIPLOMATIC',
]);
export type VisaType = z.infer<typeof VisaTypeSchema>;

export const VisaEntryTypeSchema = z.enum(['SINGLE', 'DOUBLE', 'MULTIPLE']);
export type VisaEntryType = z.infer<typeof VisaEntryTypeSchema>;

/**
 * ADR-0062. Orthogonal to `VisaType`: describes HOW the visa is processed,
 * not what kind of visa it is. Existing rows default to STANDARD - the safe
 * no-appointment path. See the enum comment in the prisma schema for the
 * per-value semantics.
 */
export const VisaProcessingModeSchema = z.enum([
  'STANDARD',
  'APPOINTMENT_ONLY',
  'APPOINTMENT_EMBASSY',
]);
export type VisaProcessingMode = z.infer<typeof VisaProcessingModeSchema>;

export const VisaStatusSchema = z.enum([
  'DRAFT',
  'PENDING_APPROVAL',
  'ACTIVE',
  'INACTIVE',
  'ARCHIVED',
]);
export type VisaStatus = z.infer<typeof VisaStatusSchema>;

/** A money amount: non-negative number with up to 2 decimal places. */
const PriceSchema = z.number().nonnegative();

// ---------------------------------------------------------------------------
// VisaPricingTier (child of Visa)
// ---------------------------------------------------------------------------

export const VisaPricingTierOutSchema = z.object({
  id: UuidSchema,
  visaId: UuidSchema,
  legacyId: z.number().int().nullable(),
  tierName: z.string(),
  processingDays: z.number().int(),
  currencyId: UuidSchema.nullable(),
  currencyCode: z.string().nullable(),
  feePerApplicant: z.number(),
  agencyCharge: z.number(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type VisaPricingTierOut = z.infer<typeof VisaPricingTierOutSchema>;

const VisaPricingTierCoreFieldsSchema = z.object({
  tierName: z.string().trim().min(1).max(80),
  processingDays: z.number().int().min(0).max(365).optional(),
  currencyId: UuidSchema.nullable().optional(),
  feePerApplicant: PriceSchema.optional(),
  agencyCharge: PriceSchema.optional(),
  isActive: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
});

export const CreateVisaPricingTierRequestSchema = VisaPricingTierCoreFieldsSchema;
export type CreateVisaPricingTierRequest = z.infer<typeof CreateVisaPricingTierRequestSchema>;

export const UpdateVisaPricingTierRequestSchema = VisaPricingTierCoreFieldsSchema.partial();
export type UpdateVisaPricingTierRequest = z.infer<typeof UpdateVisaPricingTierRequestSchema>;

// ---------------------------------------------------------------------------
// Visa (master)
// ---------------------------------------------------------------------------

export const VisaPhotoSchema = z
  .object({
    /** Media-library asset id (preferred - survives presigned-URL expiry). */
    mediaId: z.string().uuid().optional(),
    /** Direct URL. Legacy imports only; new uploads store mediaId. */
    url: z.string().url().optional(),
    caption: z.string().max(200).nullable().optional(),
    alt: z.string().max(200).nullable().optional(),
    sortOrder: z.number().int().min(0).optional(),
  })
  .refine((p) => Boolean(p.mediaId || p.url), { message: 'Photo needs a mediaId or a url' });
export type VisaPhoto = z.infer<typeof VisaPhotoSchema>;
export const VisaPhotoListSchema = z.array(VisaPhotoSchema);

export const VisaListItemSchema = z.object({
  id: UuidSchema,
  legacyId: z.number().int().nullable(),
  legacyCode: z.string().nullable(),
  slug: z.string().nullable(),
  status: VisaStatusSchema,
  name: z.string(),
  visaType: VisaTypeSchema,
  entryType: VisaEntryTypeSchema,
  processingMode: VisaProcessingModeSchema,
  targetCountryId: UuidSchema.nullable(),
  targetCountryName: z.string(),
  validityMonths: z.number().int(),
  maxStayDays: z.number().int(),
  vendorSupplierId: UuidSchema.nullable(),
  vendorSupplierName: z.string().nullable(),
  pricingTierCount: z.number().int(),
  /**
   * Default pricing tier (lowest sortOrder among active tiers) surfaced on
   * the list row so the itinerary-builder picker can show a starting price
   * without a second round-trip per row. Null when the visa has no active
   * tier yet.
   */
  defaultTierName: z.string().nullable(),
  /**
   * `feePerApplicant` of the default tier, FX-converted to INR paise so the
   * admin sees the same starting number the customer would eventually pay.
   * Null when no tier or the fee is zero. Agency-charge is intentionally
   * excluded here (it's a flat per-quote charge, not a per-traveller one -
   * shown on the quote breakdown, not on picker cards).
   */
  defaultTierFeePerApplicantInrPaise: z.string().nullable(),
  isApprovedByAdmin: z.boolean(),
  photoCount: z.number().int(),
  createdAt: IsoDateTimeSchema,
});
export type VisaListItem = z.infer<typeof VisaListItemSchema>;

export const VisaListQuerySchema = PaginationQuerySchema.extend({
  status: VisaStatusSchema.optional(),
  visaType: VisaTypeSchema.optional(),
  entryType: VisaEntryTypeSchema.optional(),
  processingMode: VisaProcessingModeSchema.optional(),
  targetCountryId: UuidSchema.optional(),
  vendorSupplierId: UuidSchema.optional(),
  isApprovedByAdmin: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type VisaListQuery = z.infer<typeof VisaListQuerySchema>;

export const VisaListResponseSchema = paginatedResponseSchema(VisaListItemSchema);
export type VisaListResponse = z.infer<typeof VisaListResponseSchema>;

export const VisaDetailSchema = VisaListItemSchema.extend({
  requiredDocuments: z.array(z.string()),
  eligibleNationalities: z.array(z.string()),
  excludedNationalities: z.array(z.string()),
  contractRef: z.string().nullable(),
  shortDescription: z.string().nullable(),
  longDescriptionHtml: z.string().nullable(),
  termsHtml: z.string().nullable(),
  cancellationHtml: z.string().nullable(),
  validityFrom: z.string().nullable(),
  validityTo: z.string().nullable(),
  photos: z.unknown().nullable(),
  isApprovedByModerator: z.boolean(),
  lastApprovedAt: IsoDateTimeSchema.nullable(),
  metaTagName: z.string().nullable(),
  pricingTiers: z.array(VisaPricingTierOutSchema),
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type VisaDetail = z.infer<typeof VisaDetailSchema>;

const VisaCoreFieldsSchema = z.object({
  status: VisaStatusSchema.optional(),
  legacyCode: z.string().trim().max(40).nullable().optional(),
  slug: z.string().trim().min(1).max(160).nullable().optional(),
  // Identity
  name: z.string().trim().min(1).max(300),
  visaType: VisaTypeSchema.optional(),
  entryType: VisaEntryTypeSchema.optional(),
  processingMode: VisaProcessingModeSchema.optional(),
  // Target - country chosen via the FK picker; name snapshot is filled
  // server-side from the selected id.
  targetCountryId: UuidSchema.nullable().optional(),
  targetCountryName: z.string().trim().max(120).optional(),
  validityMonths: z.number().int().min(0).max(120).optional(),
  maxStayDays: z.number().int().min(0).max(365).optional(),
  // Documentation
  requiredDocuments: z.array(z.string().trim().min(1).max(200)).optional(),
  eligibleNationalities: z.array(z.string().trim().min(2).max(3).toUpperCase()).optional(),
  excludedNationalities: z.array(z.string().trim().min(2).max(3).toUpperCase()).optional(),
  // Vendor
  vendorSupplierId: UuidSchema.nullable().optional(),
  contractRef: z.string().trim().max(120).nullable().optional(),
  // Content
  shortDescription: z.string().max(1000).nullable().optional(),
  longDescriptionHtml: z.string().max(50_000).nullable().optional(),
  termsHtml: z.string().max(20_000).nullable().optional(),
  cancellationHtml: z.string().max(20_000).nullable().optional(),
  // Validity
  validityFrom: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  validityTo: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  // Media
  photos: VisaPhotoListSchema.nullable().optional(),
  // Misc
  metaTagName: z.string().trim().max(200).nullable().optional(),
});

export const CreateVisaRequestSchema = VisaCoreFieldsSchema;
export type CreateVisaRequest = z.infer<typeof CreateVisaRequestSchema>;

export const UpdateVisaRequestSchema = VisaCoreFieldsSchema.partial();
export type UpdateVisaRequest = z.infer<typeof UpdateVisaRequestSchema>;

export const VisaApprovalRequestSchema = z.object({
  tier: z.enum(['admin', 'moderator']),
  approve: z.boolean(),
});
export type VisaApprovalRequest = z.infer<typeof VisaApprovalRequestSchema>;
